import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import {
  ArrowRight, Boxes, Factory, BarChart3, ScanLine, ShieldCheck, Layers3, CheckCircle2,
  Moon, Sun,
} from "lucide-react";

export default function Landing() {
  const { theme, toggleTheme } = useTheme();
  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center">
            <OperonLogo theme={theme} size={38} />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} title="Toggle theme">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button asChild variant="ghost" data-testid="landing-login-button"><Link to="/login">Log in</Link></Button>
            <Button asChild data-testid="landing-register-button"><Link to="/register">Start free trial</Link></Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 operon-navy-band pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Premium Operations SaaS
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
              Smart control for stock <br /> &amp; operations.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
              Operon is the modern ERP alternative for inventory, warehousing and manufacturing.
              Real-time stock, barcode scanning, production orders with BOM logic, branded PDFs, and reports — all in one place.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="hero-register-button">
                <Link to="/register">Start 14-day free trial <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="ghost" size="lg"><Link to="/login">I have an account</Link></Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> No credit card required</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Cancel anytime</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Full feature access during trial</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FeatureCard icon={Boxes} title="Live Inventory" body="SKUs, barcodes, images, locations. Search/filter in real time." />
          <FeatureCard icon={ScanLine} title="Barcode / QR" body="Scan on mobile for goods in, goods out and quick lookup." />
          <FeatureCard icon={Factory} title="Production Orders" body="Multi-line orders, Draft→Confirmed→In Progress→Completed." />
          <FeatureCard icon={Layers3} title="BOM Auto-Deduct" body="Define packaging recipes; stock is deducted on completion." />
          <FeatureCard icon={BarChart3} title="Reports &amp; PDFs" body="Inventory, movements, production history — PDF &amp; Excel." />
          <FeatureCard icon={ShieldCheck} title="Multi-tenant SaaS" body="Company-isolated data. Audit trail on every action." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Pricing</h2>
        <p className="text-muted-foreground text-sm mt-1">Pick a plan for your team. Upgrade anytime.</p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlanTeaser name="Starter" price="49" body="Inventory + Goods In/Out. Up to 2,000 SKUs." />
          <PlanTeaser name="Growth" price="179" body="Production Orders, BOM, Multi-user, Branding." recommended />
          <PlanTeaser name="Enterprise Mfg" price="299" body="Multi-site, batch tracking, advanced analytics." />
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Operon — Smart Control for Stock &amp; Operations</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" data-testid="landing-privacy-link">Privacy Policy</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/register">Sign up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-3 font-heading text-base font-semibold">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{body}</div>
    </div>
  );
}

function PlanTeaser({ name, price, body, recommended }) {
  return (
    <div className={`rounded-2xl border bg-card p-5 shadow-sm ${recommended ? "ring-2 ring-primary/30 border-primary/30" : "border-border"}`}>
      {recommended && <div className="inline-block rounded-full bg-primary text-primary-foreground text-[10px] px-2 py-0.5 mb-2 font-medium">Recommended</div>}
      <div className="font-heading text-lg font-semibold">{name}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-semibold tabular-nums">€{price}</span>
        <span className="text-xs text-muted-foreground">/ month</span>
      </div>
      <p className="text-sm text-muted-foreground mt-2">{body}</p>
    </div>
  );
}
